import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCashRegister, faMoneyBillWave, faExchangeAlt, faBalanceScale, faFilter, faFileExcel, faEye, faTimes, faUsers } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import baseURL from '../../url';
import moneda from '../../moneda';
import { usePlan } from '../../PlanContext';
import './ReporteCajas.css';

export default function ReporteCajas() {
    const { isPlus } = usePlan();
    const [cajas, setCajas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState({ desde: '', hasta: '', estado: '', idTrabajador: '' });
    const [trabajadores, setTrabajadores] = useState([]);
    const [detalle, setDetalle] = useState(null);
    const [cargandoDetalle, setCargandoDetalle] = useState(false);

    const abrirDetalle = (idCaja) => {
        setCargandoDetalle(true);
        setDetalle({});
        fetch(`${baseURL}/cajaDetalleGet.php?idCaja=${idCaja}`)
            .then(r => r.json())
            .then(d => { setDetalle(d.caja || null); setCargandoDetalle(false); })
            .catch(() => { setDetalle(null); setCargandoDetalle(false); });
    };

    useEffect(() => {
        fetch(`${baseURL}/usuariosGet.php`)
            .then(r => r.json())
            .then(d => setTrabajadores((d.usuarios || []).filter(u => u.rol === 'trabajador')))
            .catch(() => {});
    }, []);

    const cargar = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (filtro.desde) params.append('desde', filtro.desde);
        if (filtro.hasta) params.append('hasta', filtro.hasta);
        if (filtro.estado) params.append('estado', filtro.estado);
        if (filtro.idTrabajador) params.append('idTrabajador', filtro.idTrabajador);
        fetch(`${baseURL}/cajasGet.php?${params}`)
            .then(r => r.json())
            .then(d => { setCajas(d.cajas || []); setLoading(false); })
            .catch(() => setLoading(false));
    };

    useEffect(() => { cargar(); }, []);

    const stats = useMemo(() => {
        const totalCajas = cajas.length;
        const abiertas = cajas.filter(c => c.estado === 'abierta').length;
        const cerradas = cajas.filter(c => c.estado === 'cerrada').length;
        const ventasEfectivo = cajas.reduce((s, c) => s + Number(c.ventasEfectivo || 0), 0);
        const ventasTransferencia = cajas.reduce((s, c) => s + Number(c.ventasTransferencia || 0), 0);
        const sobrantes = cajas.filter(c => Number(c.diferencia) > 0).reduce((s, c) => s + Number(c.diferencia), 0);
        const faltantes = cajas.filter(c => Number(c.diferencia) < 0).reduce((s, c) => s + Math.abs(Number(c.diferencia)), 0);
        const cajasConProblema = cajas.filter(c => Number(c.diferencia) !== 0 && c.estado === 'cerrada').length;
        return { totalCajas, abiertas, cerradas, ventasEfectivo, ventasTransferencia, sobrantes, faltantes, cajasConProblema };
    }, [cajas]);

    const fmt = (n) => Number(n || 0).toLocaleString('es-AR');
    const fmtFecha = (f) => f ? new Date(f.replace(' ', 'T')).toLocaleString('es-AR') : '—';

    const exportar = () => {
        const wb = XLSX.utils.book_new();
        const data = cajas.map(c => ({
            'ID': c.idCaja,
            'Trabajador': c.nombreTrabajador || '—',
            'Sucursal': c.nombreSucursal || '—',
            'Apertura': fmtFecha(c.fechaApertura),
            'Cierre': fmtFecha(c.fechaCierre),
            'Monto inicial': Number(c.montoInicial || 0),
            'Ventas efectivo': Number(c.ventasEfectivo || 0),
            'Ventas transferencia': Number(c.ventasTransferencia || 0),
            'Monto esperado': Number(c.montoInicial || 0) + Number(c.ventasEfectivo || 0) + Number(c.ventasTransferencia || 0),
            'Monto final': Number(c.montoFinal || 0) + Number(c.montoTransferenciaFinal || 0),
            'Diferencia': Number(c.diferencia || 0),
            'Estado': c.estado,
            'Nota': c.nota || '',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [
            { wch: 6 }, { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 18 },
            { wch: 13 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 13 },
            { wch: 12 }, { wch: 10 }, { wch: 30 },
        ];
        XLSX.utils.book_append_sheet(wb, ws, 'Cajas');

        const resumen = [
            { 'Metrica': 'Total cajas', 'Valor': stats.totalCajas },
            { 'Metrica': 'Cajas abiertas', 'Valor': stats.abiertas },
            { 'Metrica': 'Cajas cerradas', 'Valor': stats.cerradas },
            { 'Metrica': `Ventas en efectivo (${moneda})`, 'Valor': Math.round(stats.ventasEfectivo) },
            { 'Metrica': `Ventas por transferencia (${moneda})`, 'Valor': Math.round(stats.ventasTransferencia) },
            { 'Metrica': `Total sobrantes (${moneda})`, 'Valor': Math.round(stats.sobrantes) },
            { 'Metrica': `Total faltantes (${moneda})`, 'Valor': Math.round(stats.faltantes) },
            { 'Metrica': 'Cajas con diferencia', 'Valor': stats.cajasConProblema },
        ];
        const ws2 = XLSX.utils.json_to_sheet(resumen);
        ws2['!cols'] = [{ wch: 32 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Resumen');

        XLSX.writeFile(wb, `Reporte_Cajas_${filtro.desde || 'todos'}_${filtro.hasta || 'todos'}.xlsx`);
    };

    return (
        <div className='rcContainer'>
            <div className='rcHeader'>
                <h3 className='rcTitulo'>
                    <FontAwesomeIcon icon={faCashRegister} /> Reportes de Caja
                </h3>
                <button className='rcExportBtn' onClick={exportar}>
                    <FontAwesomeIcon icon={faFileExcel} /> Exportar Excel
                </button>
            </div>

            <div className='rcFiltros'>
                <div className='rcFiltroItem'>
                    <label>Desde</label>
                    <input type='date' value={filtro.desde} onChange={e => setFiltro({ ...filtro, desde: e.target.value })} />
                </div>
                <div className='rcFiltroItem'>
                    <label>Hasta</label>
                    <input type='date' value={filtro.hasta} onChange={e => setFiltro({ ...filtro, hasta: e.target.value })} />
                </div>
                <div className='rcFiltroItem'>
                    <label>Estado</label>
                    <select value={filtro.estado} onChange={e => setFiltro({ ...filtro, estado: e.target.value })}>
                        <option value=''>Todos</option>
                        <option value='abierta'>Abierta</option>
                        <option value='cerrada'>Cerrada</option>
                    </select>
                </div>
                <div className='rcFiltroItem'>
                    <label>Trabajador</label>
                    <select value={filtro.idTrabajador} onChange={e => setFiltro({ ...filtro, idTrabajador: e.target.value })}>
                        <option value=''>Todos</option>
                        {trabajadores.map(t => (
                            <option key={t.idUsuario} value={t.idUsuario}>{t.nombre}</option>
                        ))}
                    </select>
                </div>
                <button className='rcAplicarBtn' onClick={cargar}>
                    <FontAwesomeIcon icon={faFilter} /> Aplicar
                </button>
            </div>

            <div className='rcKpis'>
                <div className='rcKpi rcKpiCajas'>
                    <FontAwesomeIcon icon={faCashRegister} />
                    <div>
                        {isPlus ? (
                            <>
                                <span className='rcKpiLabel'>Total cajas</span>
                                <span className='rcKpiValor'>{stats.totalCajas}</span>
                                <span className='rcKpiSub'>{stats.abiertas} abiertas · {stats.cerradas} cerradas</span>
                            </>
                        ) : (
                            <>
                                <span className='rcKpiLabel'>Caja</span>
                                <span className='rcKpiValor'>{stats.abiertas > 0 ? 'Abierta' : 'Cerrada'}</span>
                                <span className='rcKpiSub'>{stats.cerradas} {stats.cerradas === 1 ? 'cierre histórico' : 'cierres históricos'}</span>
                            </>
                        )}
                    </div>
                </div>
                <div className='rcKpi rcKpiEfectivo'>
                    <FontAwesomeIcon icon={faMoneyBillWave} />
                    <div>
                        <span className='rcKpiLabel'>Ventas efectivo</span>
                        <span className='rcKpiValor'>{moneda} {fmt(stats.ventasEfectivo)}</span>
                    </div>
                </div>
                <div className='rcKpi rcKpiTransf'>
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <div>
                        <span className='rcKpiLabel'>Ventas transferencia</span>
                        <span className='rcKpiValor'>{moneda} {fmt(stats.ventasTransferencia)}</span>
                    </div>
                </div>
                <div className='rcKpi rcKpiDiff'>
                    <FontAwesomeIcon icon={faBalanceScale} />
                    <div>
                        <span className='rcKpiLabel'>Diferencias</span>
                        <span className='rcKpiValor'>{stats.cajasConProblema} {stats.cajasConProblema === 1 ? 'caja' : 'cajas'}</span>
                        <span className='rcKpiSub'>
                            <span style={{ color: '#28a745' }}>+{moneda} {fmt(stats.sobrantes)}</span>
                            {' · '}
                            <span style={{ color: '#dc3545' }}>-{moneda} {fmt(stats.faltantes)}</span>
                        </span>
                    </div>
                </div>
            </div>

            <div className='rcSeccion'>
                <h4>Historial de cajas</h4>
                {loading ? (
                    <p className='rcVacio'>Cargando...</p>
                ) : cajas.length === 0 ? (
                    <p className='rcVacio'>Sin cajas en el período seleccionado</p>
                ) : (
                    <div className='rcTablaWrap'>
                        <table className='rcTabla'>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Trabajador</th>
                                    <th>Sucursal</th>
                                    <th>Apertura</th>
                                    <th>Cierre</th>
                                    <th>Inicial</th>
                                    <th>Efectivo</th>
                                    <th>Transf.</th>
                                    <th>Esperado</th>
                                    <th>Final</th>
                                    <th>Diferencia</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cajas.map(c => {
                                    const esperado = Number(c.montoInicial || 0) + Number(c.ventasEfectivo || 0) + Number(c.ventasTransferencia || 0);
                                    const diff = Number(c.diferencia || 0);
                                    return (
                                        <tr key={c.idCaja}>
                                            <td>{c.idCaja}</td>
                                            <td>{c.nombreTrabajador || '—'}</td>
                                            <td>{c.nombreSucursal || '—'}</td>
                                            <td>{fmtFecha(c.fechaApertura)}</td>
                                            <td>{fmtFecha(c.fechaCierre)}</td>
                                            <td>{moneda} {fmt(c.montoInicial)}</td>
                                            <td>{moneda} {fmt(c.ventasEfectivo)}</td>
                                            <td>{moneda} {fmt(c.ventasTransferencia)}</td>
                                            <td>{moneda} {fmt(esperado)}</td>
                                            <td>{c.montoFinal != null ? `${moneda} ${fmt(Number(c.montoFinal || 0) + Number(c.montoTransferenciaFinal || 0))}` : '—'}</td>
                                            <td style={{
                                                color: c.estado === 'abierta' ? '#6c757d' : diff === 0 ? '#28a745' : diff > 0 ? '#ffc107' : '#dc3545',
                                                fontWeight: 700,
                                            }}>
                                                {c.estado === 'abierta' ? '—' : diff > 0 ? `+${moneda} ${fmt(diff)}` : diff < 0 ? `-${moneda} ${fmt(Math.abs(diff))}` : 'Cuadrada'}
                                            </td>
                                            <td>
                                                <span className={`rcEstado rcEstado${c.estado}`}>
                                                    {c.estado.toUpperCase()}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => abrirDetalle(c.idCaja)}
                                                    title='Ver detalle'
                                                    style={{
                                                        background: '#0d6efd', color: '#fff', border: 'none',
                                                        padding: '4px 10px', borderRadius: '4px', cursor: 'pointer',
                                                        fontSize: '11px',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faEye} /> Ver
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {detalle !== null && (
                <div style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(15,20,35,0.85)',
                    zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '20px',
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '12px', padding: '24px',
                        maxWidth: '780px', width: '100%', maxHeight: '90vh', overflowY: 'auto',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <h3 style={{ margin: 0 }}>
                                <FontAwesomeIcon icon={faCashRegister} /> Detalle de caja #{detalle.idCaja || '...'}
                            </h3>
                            <button onClick={() => setDetalle(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6c757d' }}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        {cargandoDetalle ? (
                            <p>Cargando...</p>
                        ) : detalle.idCaja ? (
                            <>
                                <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                        <div><strong>Abrió:</strong> {detalle.nombreTrabajador || '—'}</div>
                                        <div><strong>Sucursal:</strong> {detalle.nombreSucursal || '—'}</div>
                                        <div><strong>Apertura:</strong> {fmtFecha(detalle.fechaApertura)}</div>
                                        <div><strong>Cierre:</strong> {fmtFecha(detalle.fechaCierre)}</div>
                                        <div><strong>Inicial:</strong> {moneda} {fmt(detalle.montoInicial)}</div>
                                        <div><strong>Transferencias:</strong> {detalle.montoTransferenciaFinal != null ? `${moneda} ${fmt(detalle.montoTransferenciaFinal)}` : '—'}</div>
                                        <div><strong>Final (total):</strong> {detalle.montoFinal != null ? `${moneda} ${fmt(Number(detalle.montoFinal || 0) + Number(detalle.montoTransferenciaFinal || 0))}` : '—'}</div>
                                    </div>
                                    {detalle.miembros?.length > 0 && (
                                        <div style={{ marginTop: '8px' }}>
                                            <strong>En caja:</strong> {detalle.miembros.map(m => m.nombre).join(', ')}
                                        </div>
                                    )}
                                    {detalle.nota && <div style={{ marginTop: '8px' }}><strong>Nota:</strong> {detalle.nota}</div>}
                                </div>

                                {detalle.esperadoPorMetodo?.length > 0 && (
                                    <>
                                        <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>
                                            <FontAwesomeIcon icon={faExchangeAlt} /> Transferencias por método
                                        </h4>
                                        <table style={{ width: '100%', fontSize: '13px', marginBottom: '16px', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f8f9fa' }}>
                                                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Método</th>
                                                    <th style={{ textAlign: 'right', padding: '6px 8px' }}>Esperado</th>
                                                    <th style={{ textAlign: 'right', padding: '6px 8px' }}>Declarado</th>
                                                    <th style={{ textAlign: 'right', padding: '6px 8px' }}>Diferencia</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detalle.esperadoPorMetodo.map((e, i) => {
                                                    const declarado = detalle.transferenciasPorMetodo?.[e.metodo] != null ? Number(detalle.transferenciasPorMetodo[e.metodo]) : null;
                                                    const dif = declarado != null ? declarado - Number(e.monto || 0) : null;
                                                    return (
                                                        <tr key={i} style={{ borderTop: '1px solid #e9ecef' }}>
                                                            <td style={{ padding: '6px 8px' }}>{e.metodo}</td>
                                                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{moneda} {fmt(e.monto)}</td>
                                                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{declarado != null ? `${moneda} ${fmt(declarado)}` : '—'}</td>
                                                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: dif == null ? '#6c757d' : dif === 0 ? '#28a745' : dif > 0 ? '#ffc107' : '#dc3545' }}>
                                                                {dif == null ? '—' : dif === 0 ? 'Cuadrado' : (dif > 0 ? `+${moneda} ${fmt(dif)}` : `-${moneda} ${fmt(Math.abs(dif))}`)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </>
                                )}

                                <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>
                                    <FontAwesomeIcon icon={faUsers} /> Ventas por trabajador
                                </h4>
                                {detalle.ventasPorTrabajador?.length ? (
                                    <table style={{ width: '100%', fontSize: '13px', marginBottom: '16px', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ background: '#f8f9fa' }}>
                                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Trabajador</th>
                                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#28a745' }}>Efectivo</th>
                                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#0d6efd' }}>Transf.</th>
                                                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Total</th>
                                                <th style={{ textAlign: 'right', padding: '6px 8px' }}>#</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detalle.ventasPorTrabajador.map((t, i) => (
                                                <tr key={i} style={{ borderTop: '1px solid #e9ecef' }}>
                                                    <td style={{ padding: '6px 8px' }}>{t.nombre || '—'}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{moneda} {fmt(t.efectivo)}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{moneda} {fmt(t.transferencia)}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{moneda} {fmt(Number(t.efectivo) + Number(t.transferencia))}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{t.totalPedidos}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : <p style={{ color: '#6c757d', fontSize: '13px' }}>Sin ventas en esta caja</p>}

                                <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>Pedidos ({detalle.pedidos?.length || 0})</h4>
                                {detalle.pedidos?.length ? (
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa' }}>
                                                <tr>
                                                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>#</th>
                                                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Trabajador</th>
                                                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Mesa</th>
                                                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Cliente</th>
                                                    <th style={{ textAlign: 'right', padding: '6px 8px' }}>Total</th>
                                                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Pago</th>
                                                    <th style={{ textAlign: 'left', padding: '6px 8px' }}>Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detalle.pedidos.map(p => (
                                                    <tr key={p.idPedido} style={{ borderTop: '1px solid #e9ecef' }}>
                                                        <td style={{ padding: '6px 8px' }}>#{p.idPedido}</td>
                                                        <td style={{ padding: '6px 8px' }}>{p.nombreTrabajador || '—'}</td>
                                                        <td style={{ padding: '6px 8px' }}>{p.mesaDescripcion === 'Mesa General' ? 'General' : (p.mesaNumero ? `Mesa ${p.mesaNumero}` : '—')}</td>
                                                        <td style={{ padding: '6px 8px' }}>{p.cliente || '—'}</td>
                                                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{moneda} {fmt(p.total)}</td>
                                                        <td style={{ padding: '6px 8px' }}>{p.pago || '—'}</td>
                                                        <td style={{ padding: '6px 8px' }}>{p.estado}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : <p style={{ color: '#6c757d', fontSize: '13px' }}>Sin pedidos en esta caja</p>}
                            </>
                        ) : <p>No se pudo cargar el detalle</p>}
                    </div>
                </div>
            )}
        </div>
    );
}
